
import Vue from 'vue';
import { ListData, HeaderActions } from '@root/modules/navigation/types/navigation';
import CustomerMenuDropdown from './CustomerMenuDropdown.vue';
import HeaderBurger from './HeaderBurger.vue';
import rootConfig from '@root/config/root.config';
import { PianoCustomerProfile } from '@piano/types/pianoState';
import { faMagnifyingGlass } from '@delfi.lt/fa-kit/icons/modules/classic/solid';
import { faCircleUser, faCircleUserCircleCheck } from '@delfi.lt/fa-kit/icons/modules/classic/regular';
import isMobileView from '@root/common/utils/isMobileView';

interface Props {
  misc: Record<string, ListData>;
}

interface Methods {
  handleOpenLoginModal: () => void;
  closeMenu: () => void;
  logout: () => void;
  openLoginModal: () => void;
  toggleSidebar: () => void;
  toggleLogin: () => void;
  toggleAction: (type: HeaderActions['type'], value: HeaderActions['value']) => void;
}

interface Computed {
  isLoggedIn: boolean;
  isSitemapOpen: boolean;
  isSearchOpen: boolean;
  isProfileInfoOpen: boolean;
  isSidebarOpen: boolean;
  profile: PianoCustomerProfile | null;
  userName: string | null;
  backgroundColor: string | null;
  disableLogin: boolean;
  isMobileView: boolean;
}

export default Vue.extend<unknown, Methods, Computed, Props>({
  components: {
    CustomerMenuDropdown,
    HeaderBurger,
  },
  props: {
    misc: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showCustomerMenuDropdown: false,
      faMagnifyingGlass,
      faCircleUser,
      faCircleUserCircleCheck,
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.state.piano.isLoggedIn;
    },
    isSitemapOpen() {
      return this.$store.getters['navigation/getHeaderAction']('sitemap');
    },
    isSearchOpen() {
      return this.$store.getters['navigation/getHeaderAction']('search');
    },
    isProfileInfoOpen() {
      return this.$store.getters['navigation/getHeaderAction']('profileInfo');
    },
    isSidebarOpen() {
      return this.$store.getters['navigation/getHeaderAction']('sidebar');
    },
    profile() {
      return this.$store.state.piano.profile;
    },
    userName() {
      if (!this.isLoggedIn || !this.profile) {
        return null;
      }

      return this.$store.state.piano.profile?.firstName
        ? this.$store.state.piano.profile?.firstName
        : this.$store.state.piano.extendedProfile?.displayName
          ? this.$store.state.piano.extendedProfile?.displayName
          : this.$store.state.piano.profile?.email.split('@')[0];
    },
    backgroundColor() {
      if (!this.isLoggedIn) {
        return null;
      }

      if (this.userName) {
        let hash = 0;
        let bgColor = null;

        for (let i = 0; i < this.userName.length; i += 1) {
          hash = this.userName.charCodeAt(i) + ((hash << 5) - hash);
        }

        const c = (hash & 0x00ffffff).toString(16).toUpperCase();
        bgColor = '00000'.substring(0, 6 - c.length) + c;

        return `#${bgColor}`;
      }

      return null;
    },
    disableLogin() {
      const { specialFeatures } = this.$channelConfig('settings').application;
      return rootConfig.disableLogin || (!!this.$channelConfig('settings').disableLogin && this.isMobileView) || specialFeatures.hideSignupAndOrders.enabled;
    },
    isMobileView() {
      return isMobileView();
    },
  },
  methods: {
    handleOpenLoginModal() {
      this.$event.send('link-click', { name: this.$t('login.log_in'), type: `header-actions` });
      this.openLoginModal();
    },
    toggleLogin() {
      this.openLoginModal();
    },
    toggleAction(type, value) {
      this.$store.commit('navigation/setHeaderAction', { type, value });
    },
    closeMenu() {
      if (!this.isSitemapOpen) {
        return;
      }
      this.toggleAction('sitemap', false);
    },
    toggleSidebar() {
      this.toggleAction('sidebar', !this.isSidebarOpen);
    },
    openLoginModal() {
      this.$store.dispatch('piano/showLoginModal', { screen: 'login' });
    },
    logout() {
      this.$store.dispatch('piano/logout');
    },
  },
});
